/**
 * JpkgReportMockView — Build / Package Quality Report
 * MOCK — design prototype only. No real data yet.
 *
 * Architecture intent:
 *   Data sources (all already in S3 post-pipeline):
 *     • SvcGen manifest.json           → services_generated, interfaces_generated, entities_generated
 *     • DM interface_contracts.json    → contracts_expected, contracts_satisfied
 *     • Code Analysis IR               → cobol_programs, data_items_total
 *     • JPkg validation_report.json    → compile_status, error_count, warning_count
 *     • JPkg package_statistics.json  → total_files, package_size_bytes
 *     • Pre-Flight overlay history     → user_fix_count
 *
 *   This report is generated server-side after JPkg completes — a lightweight
 *   Python aggregator reads the above artifacts from S3 and produces
 *   build_quality_report.json. No new pipeline node needed.
 */

import { useState } from 'react'
import {
  CheckCircle2, XCircle, AlertTriangle, Package, Download,
  ChevronDown, ChevronRight, BarChart2, Layers, GitMerge,
  Database, Cpu, Shield, FileCode, ArrowLeft,
} from 'lucide-react'
import './JpkgReportMockView.css'

// ── Mock data — realistic ClaimsModel scenario ────────────────────────────────
const MOCK = {
  project:        'ClaimsModel',
  build_id:       'jpkg_acc_65506c09_ClaimsModel_20260519_142233',
  built_at:       '2026-05-19 14:22:33 UTC',
  java_version:   '17',
  spring_version: '3.2.0',

  scores: {
    transformation:  87,   // % of COBOL programs fully represented in output
    compile:        100,   // % clean (0 errors)
    interface_sat:   92,   // % of DM contracts satisfied
    coverage:        84,   // % of COBOL data items in JPA entities
  },

  summary: {
    cobol_programs:        12,
    programs_packaged:     11,
    programs_skipped:       1,
    services_generated:    11,
    interfaces_generated:   9,
    entities_generated:    14,
    repositories_generated: 14,
    controllers_generated: 11,
    data_items_total:     347,
    data_items_covered:   291,
    contracts_expected:    12,
    contracts_satisfied:   11,
    user_fixes_applied:     7,   // overlays from Pre-Flight / Refine Code
    total_files:          189,
    package_size_kb:     2840,
  },

  compile: {
    status:   'PASSED',
    errors:    0,
    warnings:  4,
    duration_s: 18,
  },

  // Per-program coverage map
  programs: [
    { name: 'CLMMAIN',   service: 'ClaimsMainService',      interface: 'IClaimsMainService',      entities: ['ClaimRecord', 'ClaimHeader'],        status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMVAL',    service: 'ClaimsValidationService', interface: 'IClaimsValidationService', entities: ['ClaimRecord'],                       status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMPAY',    service: 'ClaimsPaymentService',   interface: 'IClaimsPaymentService',   entities: ['PaymentRecord', 'BenefitRule'],      status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMHIST',   service: 'ClaimsHistoryService',   interface: 'IClaimsHistoryService',   entities: ['ClaimHistory'],                      status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMRPT',    service: 'ClaimsReportService',    interface: 'IClaimsReportService',    entities: ['ReportDefinition'],                  status: 'warning', contracts: 'satisfied' },
    { name: 'CLMNOTIF',  service: 'ClaimsNotifyService',    interface: 'IClaimsNotifyService',    entities: ['NotificationLog'],                   status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMAUDIT',  service: 'ClaimsAuditService',     interface: 'IClaimsAuditService',     entities: ['AuditRecord'],                       status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMELIG',   service: 'ClaimsEligibilityService', interface: 'IClaimsEligibilityService', entities: ['EligibilityRecord', 'PolicyRef'], status: 'warning', contracts: 'partial'   },
    { name: 'CLMINTF',   service: 'ClaimsInterfaceService', interface: 'IClaimsInterfaceService', entities: [],                                    status: 'clean',   contracts: 'satisfied' },
    { name: 'CLMBATCH',  service: 'ClaimsBatchService',     interface: null,                      entities: ['BatchControl'],                      status: 'warning', contracts: 'n/a'       },
    { name: 'CLMARCH',   service: 'ClaimsArchiveService',   interface: null,                      entities: ['ArchiveRecord'],                     status: 'clean',   contracts: 'n/a'       },
    { name: 'CLMSHARED', service: null,                     interface: null,                      entities: [],                                    status: 'skipped', contracts: 'n/a',
      skip_reason: 'OCCURS clause translation incomplete — 3 unresolved array references. Flagged by Pre-Flight. Recommend manual review.' },
  ],

  issues: [
    { severity: 'warning', program: 'CLMRPT',  type: 'Partial entity coverage', detail: '2 of 5 COBOL data items not mapped to JPA fields — COMP-3 packed decimal fields omitted.' },
    { severity: 'warning', program: 'CLMELIG', type: 'Interface contract partial', detail: 'DM specified 3 method signatures; only 2 generated. Missing: processPolicyException().' },
    { severity: 'warning', program: 'CLMBATCH', type: 'No interface generated', detail: 'Batch control programs do not require external interfaces per DM analysis — skipped by design.' },
    { severity: 'info',    program: 'CLMSHARED', type: 'Program skipped', detail: 'OCCURS clause translation incomplete. 3 unresolved array references. See Pre-Flight history.' },
  ],

  artifacts: [
    { key: 'java_package_bundle',           label: 'Spring Boot Package',        ext: 'zip',  size: '2.8 MB', primary: true  },
    { key: 'build_quality_report',          label: 'Build Quality Report',        ext: 'json', size: '42 KB',  primary: false },
    { key: 'validation_report',             label: 'Compile Validation',          ext: 'json', size: '18 KB',  primary: false },
    { key: 'build_manifest',                label: 'Build Manifest',              ext: 'json', size: '31 KB',  primary: false },
    { key: 'package_statistics',            label: 'Package Statistics',          ext: 'json', size: '8 KB',   primary: false },
    { key: 'build_instructions',            label: 'Build & Run Instructions',    ext: 'json', size: '5 KB',   primary: false },
  ],
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function ScoreRing({ value, label, color }) {
  const r = 28, circ = 2 * Math.PI * r
  const filled = (value / 100) * circ
  return (
    <div className="jrm-ring-wrap">
      <svg width={72} height={72} viewBox="0 0 72 72">
        <circle cx={36} cy={36} r={r} fill="none" stroke="#1e2130" strokeWidth={7} />
        <circle cx={36} cy={36} r={r} fill="none" stroke={color} strokeWidth={7}
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <text x={36} y={40} textAnchor="middle" fill={color} fontSize={15} fontWeight={700}>{value}%</text>
      </svg>
      <div className="jrm-ring-label">{label}</div>
    </div>
  )
}

function StatusIcon({ status }) {
  if (status === 'clean')   return <CheckCircle2 size={13} color="#34d399" />
  if (status === 'warning') return <AlertTriangle size={13} color="#fbbf24" />
  if (status === 'skipped') return <XCircle size={13} color="#f87171" />
  return null
}

function ContractBadge({ val }) {
  if (val === 'satisfied') return <span className="jrm-badge jrm-badge-ok">satisfied</span>
  if (val === 'partial')   return <span className="jrm-badge jrm-badge-warn">partial</span>
  if (val === 'n/a')       return <span className="jrm-badge jrm-badge-na">n/a</span>
  return null
}

// ── Main component ────────────────────────────────────────────────────────────
export default function JpkgReportMockView({ onBack }) {
  const [coverageOpen, setCoverageOpen] = useState(true)
  const [issuesOpen,   setIssuesOpen]   = useState(true)
  const d = MOCK

  const overallGrade = d.scores.transformation >= 90 && d.scores.compile === 100
    ? 'A' : d.scores.transformation >= 75 ? 'B' : 'C'
  const gradeColor = overallGrade === 'A' ? '#34d399' : overallGrade === 'B' ? '#fbbf24' : '#f87171'

  return (
    <div className="jrm-root">

      {/* ── Header ── */}
      <div className="jrm-header">
        <button className="jrm-back" onClick={onBack}>
          <ArrowLeft size={14} /> Build / Package
        </button>
        <div className="jrm-header-center">
          <Package size={16} color="#4a90d9" />
          <span className="jrm-header-title">Build Quality Report</span>
          <span className="jrm-header-project">{d.project}</span>
        </div>
        <div className="jrm-header-meta">{d.built_at}</div>
      </div>

      <div className="jrm-body">

        {/* ── Overall grade + score rings ── */}
        <div className="jrm-score-panel">
          <div className="jrm-grade-wrap">
            <div className="jrm-grade" style={{ color: gradeColor, borderColor: gradeColor }}>
              {overallGrade}
            </div>
            <div className="jrm-grade-label">Transformation<br/>Grade</div>
          </div>

          <div className="jrm-rings">
            <ScoreRing value={d.scores.transformation} label="Completeness"    color="#4a90d9" />
            <ScoreRing value={d.scores.compile}         label="Compile Clean"   color="#34d399" />
            <ScoreRing value={d.scores.interface_sat}   label="Interfaces Sat." color="#a78bfa" />
            <ScoreRing value={d.scores.coverage}        label="Data Coverage"   color="#fb923c" />
          </div>

          <div className="jrm-stat-grid">
            <div className="jrm-stat"><Cpu size={13}/><span>{d.summary.cobol_programs} COBOL programs</span></div>
            <div className="jrm-stat"><Layers size={13}/><span>{d.summary.services_generated} services generated</span></div>
            <div className="jrm-stat"><GitMerge size={13}/><span>{d.summary.interfaces_generated} interfaces satisfied</span></div>
            <div className="jrm-stat"><Database size={13}/><span>{d.summary.entities_generated} JPA entities</span></div>
            <div className="jrm-stat"><FileCode size={13}/><span>{d.summary.data_items_covered}/{d.summary.data_items_total} data items mapped</span></div>
            <div className="jrm-stat"><Shield size={13}/><span>{d.summary.user_fixes_applied} user fixes applied (Pre-Flight)</span></div>
            <div className="jrm-stat"><BarChart2 size={13}/><span>{d.summary.total_files} files · {d.summary.package_size_kb} KB</span></div>
            <div className="jrm-stat" style={{ color: d.compile.errors > 0 ? '#f87171' : '#34d399' }}>
              <CheckCircle2 size={13}/>
              <span>Compile {d.compile.status} · {d.compile.errors} errors · {d.compile.warnings} warnings · {d.compile.duration_s}s</span>
            </div>
          </div>
        </div>

        {/* ── Coverage map ── */}
        <div className="jrm-section">
          <button className="jrm-section-toggle" onClick={() => setCoverageOpen(v => !v)}>
            {coverageOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <span>Program Coverage Map</span>
            <span className="jrm-section-count">{d.summary.programs_packaged} packaged · {d.summary.programs_skipped} skipped</span>
          </button>

          {coverageOpen && (
            <div className="jrm-table-wrap">
              <table className="jrm-table">
                <thead>
                  <tr>
                    <th>COBOL Program</th>
                    <th>Java Service</th>
                    <th>Interface</th>
                    <th>Entities</th>
                    <th>DM Contract</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.programs.map(p => (
                    <tr key={p.name} className={p.status === 'skipped' ? 'jrm-row-skipped' : ''}>
                      <td className="jrm-mono">{p.name}</td>
                      <td className="jrm-mono jrm-small">{p.service || <span className="jrm-na">—</span>}</td>
                      <td className="jrm-mono jrm-small">{p.interface || <span className="jrm-na">—</span>}</td>
                      <td className="jrm-entities">
                        {p.entities.length > 0
                          ? p.entities.map(e => <span key={e} className="jrm-entity-chip">{e}</span>)
                          : <span className="jrm-na">—</span>}
                      </td>
                      <td><ContractBadge val={p.contracts} /></td>
                      <td><StatusIcon status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Issues ── */}
        <div className="jrm-section">
          <button className="jrm-section-toggle" onClick={() => setIssuesOpen(v => !v)}>
            {issuesOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            <span>Transformation Issues</span>
            <span className="jrm-section-count">{d.issues.filter(i => i.severity === 'warning').length} warnings · {d.issues.filter(i => i.severity === 'info').length} info</span>
          </button>

          {issuesOpen && (
            <div className="jrm-issues">
              {d.issues.map((issue, i) => (
                <div key={i} className={`jrm-issue jrm-issue--${issue.severity}`}>
                  <div className="jrm-issue-header">
                    {issue.severity === 'warning'
                      ? <AlertTriangle size={12} />
                      : <CheckCircle2 size={12} />}
                    <span className="jrm-issue-program">{issue.program}</span>
                    <span className="jrm-issue-type">{issue.type}</span>
                  </div>
                  <div className="jrm-issue-detail">{issue.detail}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Artifacts / Downloads ── */}
        <div className="jrm-section">
          <div className="jrm-section-toggle" style={{ cursor: 'default' }}>
            <Package size={14} />
            <span>Artifacts</span>
            <span className="jrm-section-count">Java {d.java_version} · Spring Boot {d.spring_version}</span>
          </div>

          <div className="jrm-artifacts">
            {d.artifacts.map(a => (
              <div key={a.key} className={`jrm-artifact${a.primary ? ' jrm-artifact--primary' : ''}`}>
                <div className="jrm-artifact-left">
                  <span className="jrm-artifact-label">{a.label}</span>
                  <span className="jrm-artifact-meta">.{a.ext} · {a.size}</span>
                </div>
                <button className="jrm-download-btn" title={`Download ${a.label}`}>
                  <Download size={13} />
                  {a.primary ? 'Download Package' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Architecture note (mock only) ── */}
        <div className="jrm-arch-note">
          <span className="jrm-arch-badge">MOCK</span>
          This report will be generated server-side after JPkg completes — a lightweight aggregator reads
          SvcGen manifest, DM interface contracts, Code Analysis IR, and JPkg validation output from S3
          and produces <code>build_quality_report.json</code>. No new pipeline node required.
        </div>

      </div>
    </div>
  )
}
